import { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Row,
  Col,
  InputGroup,
  InputGroupText,
  FormFeedback,
  Input,
} from "reactstrap";

export function CostLimitList(props) {
  const { onChange, costLimits, limitOptions, show, toggle } = props;

  const [limits, setLimits] = useState([]);
  const [valid, setValid] = useState(false);

  const originalLimits = useRef([]);

  const addLimitsData = () => {
    const fullData = limitOptions || [];
    let data = [...costLimits] || [];
    fullData.map((item) => {
      const dataItem = data.find((d) => {
        return d.order_cost_limit_period_id === item.id;
      });
      if (dataItem) {
        item.dollar_limit = dataItem.dollar_limit || "";
        item.order_cost_limit_period_id = item.id;
        item.enabled = dataItem.enabled || dataItem.dollar_limit > 0;
        delete item.id;
        delete item.per_unit_label;
      } else {
        item.dollar_limit = item.dollar_limit > 0 ? item.dollar_limit : "";
        item.order_cost_limit_period_id =
          item.order_cost_limit_period_id || item.id;
        item.enabled = item.enabled || item.dollar_limit > 0;
        delete item.id;
        delete item.per_unit_label;
        data.push(item);
      }
      return item;
    });
    setLimits(fullData);
    if (!originalLimits.current.length) {
      originalLimits.current = fullData;
    }
  };

  useEffect(() => {
    if (limitOptions.length) {
      addLimitsData();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (limitOptions.length) {
      addLimitsData();
    }
    // eslint-disable-next-line
  }, [costLimits, limitOptions]);

  useEffect(() => { 
    if (limits.length) {
      const empty = checkIfEmpty();
      if (empty) {
        setValid(false);
      } else {
        setValid(true);
      }
    }
     // eslint-disable-next-line
  }, [limits]);

  const handleOnChange = (id, value) => {
    const enabled = value !== "" && value > 0;
    validateValue(id, value, enabled);
    const newLimits = [...limits];
    newLimits.find((d) => {
      if (d.order_cost_limit_period_id === id) {
        d.dollar_limit = value || "";
        d.order_cost_limit_period_id = id;
        d.enabled = d.enabled || d.dollar_limit > 0;
        return true;
      }
      return false;
    });
    setLimits([...newLimits]);
  };

  const validateValue = (id, value, enabled) => {
    if (limits.length === 0 || (!enabled && value !== null)) {
      return;
    }

    const systemMax = 100_000_001;
    value = parseInt(value);
    if (isNaN(value)) return;

    const periods = ["per_order", "monthly", "quarterly", "annually"];
    const limitsMap = periods.reduce((acc, period) => {
      const limit = limits.find(d => d.order_cost_limit_period_id === period) || {};
      delete limit.error;
      acc[period] = limit;
      return acc;
    }, {});

    const { per_order, monthly, quarterly, annually } = limitsMap;

    const parseLimit = (v) => {
      const n = parseInt(v);
      return isNaN(n) ? null : n;
    };

    const getMin = (...vals) => Math.max(...vals.filter(v => v !== null));
    const getMax = (...vals) => Math.min(...vals.filter(v => v !== null));

    let min = 1;
    let max = systemMax;

    const setError = (limitObj, minVal, maxVal) => {
      limitObj.error = `Value must be between $${minVal.toLocaleString("en-US")} and $${maxVal.toLocaleString("en-US")}`;
    };

    switch (id) {
      case "per_order":
        max = getMax(
          parseLimit(monthly.dollar_limit),
          parseLimit(quarterly.dollar_limit),
          parseLimit(annually.dollar_limit),
          systemMax
        );
        if (value < min || value >= max) {
          setError(per_order, min, max - 1);
        } else {
          setValid(true);
        }
        break;

      case "monthly":
        min = getMin(parseLimit(per_order.dollar_limit), 0);
        max = getMax(
          parseLimit(quarterly.dollar_limit),
          parseLimit(annually.dollar_limit),
          systemMax
        );
        if (value <= min || value >= max) {
          setError(monthly, min + 1, max - 1);
        } else {
          setValid(true);
        }
        break;

      case "quarterly":
        min = getMin(
          parseLimit(per_order.dollar_limit),
          parseLimit(monthly.dollar_limit),
          0
        );
        max = getMax(parseLimit(annually.dollar_limit), systemMax);
        if (value <= min || value >= max) {
          setError(quarterly, min + 1, max - 1);
        } else {
          setValid(true);
        }
        break;

      case "annually":
        min = getMin(
          parseLimit(per_order.dollar_limit),
          parseLimit(monthly.dollar_limit),
          parseLimit(quarterly.dollar_limit),
          0
        );
        max = systemMax - 1;
        if (value <= min || value > max) {
          setError(annually, min + 1, max);
        } else {
          setValid(true);
        }
        break;
      default:
        break;
    }
  };

  const onCancel = (cancel) => {
    if (cancel) { 
      setLimits(originalLimits.current);
    }
    toggle(cancel);
  };

  const handleOnSave = () => {
    if (limits.length && valid) {
      toggle();
      onChange(limits);
    }
  };

  const checkIfEmpty = () => {
    const empty = limits.every((item) => {
      return item.dollar_limit === "" || item.dollar_limit === 0;
    });
    return empty;
  }

  return (
    <Modal centered isOpen={show} className="order-limit-modal">
      <ModalHeader>Order Cost Limits</ModalHeader>
      <ModalBody>
        <div className="cost-limit-list">
          {limits.map((item, index) => {
            return (
              <CostLimitItem
                id={Object.keys(item)[0]}
                key={index}
                enabled={item.enabled}
                costLimit={item}
                onChange={handleOnChange}
                error={validateValue(
                  item.order_cost_limit_period_id,
                  item.dollar_limit
                )}        
              />
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => onCancel(true)}>Cancel</Button>
        <Button className="promote" onClick={handleOnSave} disabled={!valid}>
          Save Limits
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function CostLimitItem(props) {
  const { costLimit, onChange } = props;

  const [value, setValue] = useState(
    costLimit.dollar_limit > 0 && costLimit.dollar_limit !== ""
      ? costLimit.dollar_limit
      : 0
  );

  const [error] = useState(costLimit.error !== "" && costLimit.error !== undefined);

  const handleOnBlur = (e) => {
    if (costLimit.error) {
      costLimit.error = "";
      setValue(0);
    }
  };

  const handleOnChange = (e) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    if (costLimit.dollar_limit > 0 && costLimit.dollar_limit !== "") {
      setValue(costLimit.dollar_limit);
    } else {
      setValue("");
    }
    // eslint-disable-next-line
  }, [costLimit]);

    useEffect(() => {
      const rawValue = value.toString().replace(/,/g, "");
    if (!error) {
      onChange(costLimit.order_cost_limit_period_id, parseInt(rawValue) || 0);
    } else {
      onChange(costLimit.order_cost_limit_period_id, 0);
    }
    // eslint-disable-next-line
  }, [value, costLimit, error]);

  return (
    <div className="cost-limit-form ">
      <FormGroup key={costLimit.order_cost_limit_period_id}>
        <Row className="align-items-center">
          <Col xs="3">
            <Label className="mb-1 limit-label">{costLimit.label}</Label>
          </Col>
          <Col xs="9">
            <InputGroup>
              <InputGroupText>$</InputGroupText>
              <Input
                invalid={costLimit.error ? true : false}
                type="text"
                id={costLimit.order_cost_limit_period_id}
                placeholder="enter amount"
                value={costLimit.dollar_limit.toLocaleString("en-US") || ""}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
              />
              <FormFeedback>{costLimit.error}</FormFeedback>
            </InputGroup>
          </Col>
        </Row>
      </FormGroup>
    </div>
  );
}
